import * as React from 'react'
import { FC, useCallback } from 'react'
import { useSelector } from 'react-redux'

import { defaultFilters as defaultLeadFilters } from '@user/store/lead/filters'
import { defaultFilters as defaultOppFilters } from '@user/store/opportunity/filters'
import { ISalesFunnelSummaryCard } from '@user/store/salesFunnelSummary/typings'
import { ReasonsPopover } from '../SalesFunnelCard/ReasonsPopover'
import { SalesFunnelMetric } from '../SalesFunnelMetric'
import { selectSalesFunnelFilters } from '@user/store/salesFunnelSummary/slice'
import { useNavigate } from 'react-router'
import { useSaveLeadFilters } from '@user/store/lead'
import { useSaveOpportunityFilters } from '@user/store/opportunity'

import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Card from '@mui/material/Card'
import { getStyles } from './hooks/useStyles'
import { useTheme } from '@mui/material/styles'
import { Nullable } from '@user/components/Filters'
import { useSoldServiceStatuses } from './hooks/useSoldServicesStatuses'
import { buildLostOpportunityFilters } from '../utils/buildLostOpportunityFilters'

interface Props {
  summaryCard: ISalesFunnelSummaryCard
  handleClose: () => void
}

export const SalesFunnelSummaryMetrics: FC<Props> = (props) => {
  const { summaryCard, handleClose } = props

  const {
    disqualifiedLeadsCount,
    disqualifiedLeadsValue,
    disqualifiedReasons,
    lostOpportunitiesCount,
    lostOpportunitiesValue,
    lostReasons,
    openLeadsCount,
    openLeadsValue,
    openOpportunitiesCount,
    openOpportunitiesValue,
  } = summaryCard

  const navigate = useNavigate()
  const funnelFilters = useSelector(selectSalesFunnelFilters)
  const saveLeadFilters = useSaveLeadFilters()
  const saveOpportunityFilters = useSaveOpportunityFilters()
  const { data } = useSoldServiceStatuses()

  const goToLeads = useCallback(
    (isClosed: Nullable) => {
      saveLeadFilters({
        ...defaultLeadFilters,
        filters: {
          ...defaultLeadFilters.filters,
          closed: {
            ...defaultLeadFilters.filters.closed,
            value: isClosed,
          },

          createdDate:
            isClosed === 'notnull'
              ? defaultLeadFilters.filters.createdDate
              : {
                  ...defaultLeadFilters.filters.createdDate,
                  value: funnelFilters.filters.createdDate.value, // ✅ DateRange goes into value
                },
          salesTeamIds: {
            ...defaultLeadFilters.filters.salesTeamId,
            value: funnelFilters.filters.salesTeamIds.value,
          },
          assignee: {
            ...defaultLeadFilters.filters.assignee,
            value: funnelFilters.filters.ownerIds.value,
          },
          type: {
            ...defaultLeadFilters.filters.type,
            value: funnelFilters.filters.type.value,
          },
          disqualifiedDate:
            isClosed === 'null'
              ? defaultLeadFilters.filters.disqualifiedDate
              : {
                  ...defaultLeadFilters.filters.disqualifiedDate,
                  value: funnelFilters.filters.createdDate.value, // ✅ DateRange goes into value
                },
        },
      })

      navigate('/leads')
      handleClose()
    },
    [handleClose, navigate, funnelFilters, saveLeadFilters],
  )

  const goToOpps = useCallback(
    (isClosed: Nullable) => {
      const soldServiceStatuses = data ?? []
      saveOpportunityFilters({
        ...defaultOppFilters,
        filters: {
          ...defaultOppFilters.filters,
          closed: {
            ...defaultOppFilters.filters.closed,
            value: isClosed,
          },

          createdDate:
            isClosed === 'notnull'
              ? defaultOppFilters.filters.createdDate
              : {
                  ...defaultOppFilters.filters.createdDate,
                  value: funnelFilters.filters.createdDate.value, // ✅ DateRange goes into value
                },

          salesTeamIds: {
            ...defaultOppFilters.filters.salesTeamId,
            value: funnelFilters.filters.salesTeamIds.value,
          },

          assignee: {
            ...defaultOppFilters.filters.assignee,
            value: funnelFilters.filters.ownerIds.value,
          },
          type: {
            ...defaultOppFilters.filters.type,
            value: funnelFilters.filters.type.value,
          },
          soldServiceStatus:
            isClosed === 'null'
              ? defaultOppFilters.filters.soldServiceStatus
              : {
                  ...defaultOppFilters.filters.soldServiceStatus,
                  value: {
                    mode: 'none',
                    options: soldServiceStatuses,
                  },
                },
          closedDate:
            isClosed === 'null'
              ? defaultOppFilters.filters.closedDate
              : {
                  ...defaultOppFilters.filters.closedDate,
                  value: funnelFilters.filters.createdDate.value, // ✅ DateRange goes into value
                },
        },
      })

      navigate('/opportunities')
      handleClose()
    },
    [
      data,
      saveOpportunityFilters,
      funnelFilters.filters.createdDate.value,
      funnelFilters.filters.salesTeamIds.value,
      funnelFilters.filters.ownerIds.value,
      funnelFilters.filters.type.value,
      navigate,
      handleClose,
    ],
  )
  const goToLostOpps = useCallback(() => {
    saveOpportunityFilters(buildLostOpportunityFilters(funnelFilters))

    navigate('/opportunities')
    handleClose()
  }, [funnelFilters, handleClose, navigate, saveOpportunityFilters])

  const styles = getStyles(useTheme())
  return (
    <Card elevation={2} sx={styles.card}>
      <Box>
        <Grid container>
          <Grid item xs>
            <SalesFunnelMetric
              count={openLeadsCount}
              handleClick={() => goToLeads('null')}
              infoText={`Open leads created within the selected date range.`}
              title="Open Leads"
              value={openLeadsValue}
              variant="h2"
            />
          </Grid>
          <Grid item xs>
            <SalesFunnelMetric
              count={openOpportunitiesCount}
              handleClick={() => goToOpps('null')}
              infoText={`Open opportunities created within the selected date range.`}
              title="Open Opportunities"
              value={openOpportunitiesValue}
              variant="h2"
            />
          </Grid>
          <Grid item xs>
            <SalesFunnelMetric
              handleClick={() => goToLeads('notnull')}
              actionBtn={
                <ReasonsPopover
                  count={disqualifiedLeadsCount}
                  reasons={disqualifiedReasons}
                  status="disqualified"
                />
              }
              count={disqualifiedLeadsCount}
              infoText={`Leads that have been disqualified within the selected date range. Calculated using disqualified date including any leads created outside the selected date range.`}
              subtitle="Top 3 Disqualified Reasons"
              title="Disqualified"
              value={disqualifiedLeadsValue}
              variant="h3"
            />
          </Grid>
          <Grid item xs>
            <SalesFunnelMetric
              handleClick={goToLostOpps}
              actionBtn={
                <ReasonsPopover
                  count={lostOpportunitiesCount}
                  reasons={lostReasons}
                  status="lost"
                />
              }
              count={lostOpportunitiesCount}
              infoText={`Opportunities that were lost within the selected date range. Calculated using closed/lost date including opportunities created outside the selected date range that were closed/lost within the selected date range.`}
              subtitle="Top 3 Lost Reasons"
              title="Lost"
              value={lostOpportunitiesValue}
              variant="h3"
            />
          </Grid>
        </Grid>
      </Box>
    </Card>
  )
}

SalesFunnelSummaryMetrics.displayName = 'SalesFunnelSummaryMetrics'
